import { useEffect, useState } from "react";
import Input from "../../../../componentes/input/input";
import Button from "../../../../componentes/button/button";
import InputMoney from "../../../../componentes/input/inputMoney";
import DateMasked from "../../../../componentes/inputMask/DateMasked";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import RateioOrcamentarioEditor from "../shared/RateioOrcamentarioEditor";
import RateioReceitaDespesaEditor from "./RateioReceitaDespesaEditor";
import SelecionarFornecedorModal from "../shared/SelecionarFornecedorModal";
import SolicitacaoCompraService from "../../../../services/resource/solicitacaoCompraService";
import ConfiguracaoSolicitacaoService from "../../../../services/resource/configuracaoSolicitacaoService";
import { somaBateComTotal } from "../../../../utils/solicitacoes/orcamento";
import { formatarCnpjCpf } from "../../../../utils/solicitacoes/formatters";

const solicitacaoCompraService = new SolicitacaoCompraService();
const configuracaoService = new ConfiguracaoSolicitacaoService();
const CHAVE_BLOQUEIO_RECEBIMENTO = "BLOQUEAR_DESPESA_COM_COMPRA_EM_RECEBIMENTO";

/**
 * Campos da Etapa 1 de Lançamento de Despesa — reaproveitados tanto na criação
 * (NovaSolicitacaoDespesaForm.jsx, dentro do NovaSolicitacaoLayout) quanto na edição de uma
 * solicitação retificada de volta para a Etapa 1 (EtapaDespesaSolicitacaoRetificada.jsx, direto no
 * card de "Etapa atual"), para não duplicar UI/validação entre os dois contextos.
 */

export const TIPO_DESPESA_OPTIONS = [
    { value: "SUPRIMENTOS", label: "Suprimentos (NFE/NFSe/Recibo)" },
    { value: "CONTAS_A_PAGAR", label: "Contas a Pagar (Imposto, Guia FGTS)" },
];

// Contas a Pagar não usa Conta BP: o rateio é de receita/despesa (filial + centro de custo + conta financeira).
export const ehContasAPagar = form => form.tipoDespesa === "CONTAS_A_PAGAR";

/** Payload do rateio para o backend (RateioLancamentoDespesaItemRequest), conforme o tipo de despesa. */
export function montarRateioParaEnvio(form) {
    return form.rateio.map(item => ehContasAPagar(form)
        ? {
            filialId: item.filialId,
            centroCustoCodigo: item.centroCustoCodigo,
            contaFinanceiraCodigo: item.contaFinanceiraCodigo,
            valor: Number(item.valor),
            numeroNotaFiscal: item.numeroNotaFiscal,
        }
        : { baseBpId: item.baseBpId, valor: Number(item.valor), numeroNotaFiscal: item.numeroNotaFiscal });
}

/** Mesmas regras validadas no backend por EtapaDespesaSolicitacaoHandler.validar e LancamentoDespesaFluxo. */
export function validarCamposDespesa({ form }) {
    if (!form.filialId || !form.fornecedorId || !form.descricao || !form.tipoDespesa
        || !form.valorTotal || !form.vencimento || !form.tipoPagamento) {
        return "Preencha todos os campos obrigatórios.";
    }
    if (form.rateio.length === 0) {
        return "Informe pelo menos um item de rateio.";
    }
    if (ehContasAPagar(form) && form.rateio.some(item => !item.filialId || !item.centroCustoCodigo || !item.contaFinanceiraCodigo)) {
        return "Informe a filial, o centro de custo e a conta financeira em todos os itens do rateio.";
    }
    if (form.rateio.some(item => !item.valor || Number(item.valor) <= 0)) {
        return "Todos os itens do rateio devem ter um valor maior que zero.";
    }
    if (form.rateio.some(item => !item.numeroNotaFiscal || !item.numeroNotaFiscal.trim())) {
        return "Informe o número da nota fiscal em todos os itens do rateio.";
    }
    const chavesRateio = form.rateio.map(item => ehContasAPagar(form)
        ? `${item.filialId}|${item.centroCustoCodigo}|${item.contaFinanceiraCodigo}|${item.numeroNotaFiscal.trim()}`
        : `${item.baseBpId}|${item.numeroNotaFiscal.trim()}`);
    if (new Set(chavesRateio).size !== chavesRateio.length) {
        return ehContasAPagar(form)
            ? "Não é permitido repetir a mesma Filial, Centro de Custo e Conta Financeira para a mesma Nota."
            : "Não é permitido repetir a mesma Conta BP para a mesma Nota Fiscal.";
    }
    const somaRateio = form.rateio.reduce((soma, item) => soma + (Number(item.valor) || 0), 0);
    if (!somaBateComTotal(somaRateio, form.valorTotal)) {
        return "A soma do rateio deve ser igual ao valor total.";
    }
    return null;
}

export default function SolicitacaoDespesaCampos({ form, setForm, filiais, tiposPagamento, fornecedorInicial, onPendenciaRecebimentoChange }) {
    const [fornecedorSelecionado, setFornecedorSelecionado] = useState(fornecedorInicial || null);
    const [modalFornecedorAberto, setModalFornecedorAberto] = useState(false);
    const [pendentesRecebimento, setPendentesRecebimento] = useState([]);
    // Configuração da aba "Configuração" (Atribuições): ligada, compra aguardando Recebimento bloqueia o envio;
    // desligada, só avisa. Sem conseguir ler, fica ligada — o comportamento de sempre.
    const [bloqueioRecebimento, setBloqueioRecebimento] = useState(true);

    useEffect(() => {
        configuracaoService.buscar(CHAVE_BLOQUEIO_RECEBIMENTO)
            .then(res => setBloqueioRecebimento(res.data?.ativo !== false))
            .catch(() => setBloqueioRecebimento(true));
    }, []);

    // Quem usa o componente bloqueia o envio pelo que recebe aqui — com o bloqueio desligado, não recebe nada.
    useEffect(() => {
        onPendenciaRecebimentoChange?.(bloqueioRecebimento ? pendentesRecebimento : []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bloqueioRecebimento, pendentesRecebimento]);

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    // Os dois tipos de rateio têm linhas incompatíveis (Conta BP x conta financeira) — trocar o tipo zera o rateio.
    function alterarTipoDespesa(tipoDespesa) {
        if (tipoDespesa === form.tipoDespesa) return;
        if (form.rateio.length > 0
            && !window.confirm("Trocar o tipo de despesa limpa os itens de rateio já informados. Deseja continuar?")) {
            return;
        }
        setForm(prev => ({ ...prev, tipoDespesa, rateio: [] }));
    }

    function selecionarFornecedor(fornecedor) {
        setFornecedorSelecionado(fornecedor);
        set("fornecedorId", fornecedor.id);

        solicitacaoCompraService.buscarPendentesRecebimento(fornecedor.id)
            .then(res => {
                const pendentes = res.data || [];
                setPendentesRecebimento(pendentes);
            })
            .catch(() => {
                setPendentesRecebimento([]);
            });
    }

    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Filial <span className="text-destructive">*</span></label>
                    <CustomDropdown value={form.filialId} onChange={e => set("filialId", e.value)} options={filiais} optionValue="id" optionLabel="nome" placeholder="Selecione" filter />
                </div>
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Fornecedor <span className="text-destructive">*</span></label>
                    <Button
                        variant="outline"
                        label={fornecedorSelecionado ? "Trocar Fornecedor" : "Buscar Fornecedor"}
                        iconLeft="fa fa-search"
                        onClick={() => setModalFornecedorAberto(true)}
                    />
                </div>
                <Input
                    label="Fornecedor Selecionado"
                    disabled
                    value={fornecedorSelecionado ? `${fornecedorSelecionado.nome} — ${formatarCnpjCpf(fornecedorSelecionado.cnpj || fornecedorSelecionado.cpf)}` : ""}
                />
            </div>

            {pendentesRecebimento.length > 0 && (
                <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                    <i className="fa fa-exclamation-triangle text-amber-500 text-sm mt-0.5 shrink-0" />
                    <div className="min-w-0 text-sm text-amber-900">
                        Existe {pendentesRecebimento.length > 1 ? "solicitações de Compras" : "uma Solicitação de Compras"}
                        {" "}(nº {pendentesRecebimento.map(s => s.numero).join(", ")}) deste fornecedor aguardando{" "}
                        <strong>Confirmação de Recebimento</strong>.{" "}
                        {bloqueioRecebimento ? (
                            <>
                                Esta nota deve ser lançada pelo processo de <strong>Recebimento de Compras</strong> — localize e
                                conclua o recebimento da compra correspondente antes de criar um novo Lançamento de Despesa para
                                este fornecedor.
                            </>
                        ) : (
                            <>Confira se esta nota não é a mesma da compra, para não lançá-la duas vezes. O envio está liberado.</>
                        )}
                    </div>
                </div>
            )}

            <SelecionarFornecedorModal
                isOpen={modalFornecedorAberto}
                onClose={() => setModalFornecedorAberto(false)}
                onSelecionar={selecionarFornecedor}
            />

            <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                <div className="col-span-2">
                    <Input label="Descrição" required value={form.descricao} onChange={e => set("descricao", e.target.value)} />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <CustomDropdown required label="Tipo de Despesa" value={form.tipoDespesa} onChange={e => alterarTipoDespesa(e.value)} options={TIPO_DESPESA_OPTIONS} placeholder="Selecione" />
                <InputMoney
                    label="Valor Total"
                    required
                    value={form.valorTotal}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    mode="decimal"
                    min="0"
                    onFocus={(e) => e.target.select()}
                    onChange={e => set("valorTotal", e.target.value)} />
                <DateMasked type="date" label="Vencimento" required value={form.vencimento} onChange={e => set("vencimento", e)} />
                <CustomDropdown required label="Tipo de pagamento" value={form.tipoPagamento} onChange={e => set("tipoPagamento", e.value)} options={tiposPagamento} optionValue="nome" optionLabel="nome" placeholder="Selecione" filter />
            </div>

            {form.tipoPagamento === "Transferência" && (
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                    <div className="col-span-2 flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-foreground/80">Tipo de Conta <span className="text-destructive">*</span></label>
                        <CustomDropdown value={form.tipoConta} onChange={e => set("tipoConta", e.value)} options={[{ nome: "Conta Corrente" }, { nome: "Conta Poupança" }]} optionValue="nome" optionLabel="nome" placeholder="Selecione" filter />
                    </div>
                    <Input type="number" label="Agencia" required value={form.agencia} onChange={e => set("agencia", e.target.value)} />
                    <Input type="number" label="Digito" required value={form.agenciaDigito} onChange={e => set("agenciaDigito", e.target.value)} />
                    <Input type="number" label="Conta" required value={form.conta} onChange={e => set("conta", e.target.value)} />
                    <Input type="number" label="Digito" required value={form.contaDigito} onChange={e => set("contaDigito", e.target.value)} />
                </div>
            )}

            {form.tipoPagamento === "PIX" && (
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="col-span-2">
                        <Input label="Chave PIX" required value={form.chavePix} onChange={e => set("chavePix", e.target.value)} />
                    </div>
                </div>
            )}

            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-2">
                    {ehContasAPagar(form) ? "Rateio de Receita/Despesa" : "Rateio"} <span className="text-destructive">*</span>
                </label>
                {ehContasAPagar(form) ? (
                    <RateioReceitaDespesaEditor itens={form.rateio} onChange={itens => set("rateio", itens)} filiais={filiais}
                        filialPadraoId={form.filialId} valorTotal={form.valorTotal ? Number(form.valorTotal) : null} />
                ) : (
                    <RateioOrcamentarioEditor itens={form.rateio} onChange={itens => set("rateio", itens)} filiais={filiais} comValor valorTotal={form.valorTotal ? Number(form.valorTotal) : null} fornecedorId={form.fornecedorId} />
                )}
            </div>
        </>
    );
}
